import { ArrowUpOutlined } from '@ant-design/icons';
import { DownloadFileFromArray } from '@components/shared/file/FileDownloadFunctions';
import { DoneView } from '@components/shared/import/DoneView';
import { ExceptionView } from '@components/shared/import/ExceptionView';
import { LoadingView } from '@components/shared/import/LoadingView';
import { IsolatedModal } from '@components/shared/modals/IsolatedModal';
import { ImportStatus } from '@components/shared/types/importStatus';
import { ajaxCatch } from '@helper/api';
import { useRootData } from '@hooks/hook';
import { DictionariesConstants } from '@services/DictionariesConstants';
import { getEssityApiClient } from '@services/EssityApi';
import {
	ExportModelGetTemplateNoteOptionalParams,
	ExportModelGetTemplateNoteResponse,
	FileType,
	TemplateNoteDto,
} from '@services/src/models';
import { Button, Col } from 'antd';
import { Formik, FormikActions, FormikProps } from 'formik';
import { CenteredRow } from 'layout/CenteredRow';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { IDictionaryStore } from 'stores/DictionaryStore';
import { IGridStore } from 'stores/GridStore';

const ExportPaymentNoteModal = () => {
	const [visible, setVisible] = useState<boolean>(false);

	const [progressState, setProgressState] = useState<ImportStatus>(
		ImportStatus.Init
	);

	const gridStore: IGridStore = useRootData((store) => store.gridStore);

	const dictionaryStore: IDictionaryStore = useRootData(
		(store) => store.dictionaryStore
	);

	const handleCancel = () => {
		setVisible(false);
		setProgressState(ImportStatus.Init);
	};

	const InitialView = (
		props: FormikProps<ExportModelGetTemplateNoteOptionalParams>
	) => (
		<CenteredRow style={{ textAlign: 'center' }}>
			<Col span={24}>
				<h3>System will create NHF payment notes for:</h3>
			</Col>
			<Col span={24}>
				{gridStore.getSelectedValues().map((x: TemplateNoteDto) => {
					return (
						<p key={x.id}>
							<b>
								{dictionaryStore.getValueById(
									DictionariesConstants.Voivodeships,
									(x.voivodeshipId as unknown) as number
								)}
							</b>{' '}
							(Voivodeship){' '}
							<b>
								{x.month}.{x.year}
							</b>{' '}
							(Settlement period)
						</p>
					);
				})}
			</Col>
			<Button
				type="primary"
				size="large"
				shape="round"
				onClick={() => {
					if (!props.isSubmitting) {
						props.submitForm();
					}
				}}
			>
				Get NHF payment notes
			</Button>
		</CenteredRow>
	);

	const handleRender = (
		props: FormikProps<ExportModelGetTemplateNoteOptionalParams>
	) => {
		return (
			<IsolatedModal
				title="Creating NHF payment notes"
				visible={visible}
				width="30vw"
				centered
				okText="Get report file"
				cancelButtonProps={{ hidden: true }}
				okButtonProps={{ hidden: true }}
				maskClosable={false}
				onCancel={handleCancel}
				closable={true}
				destroyOnClose
			>
				{getViewByExportStatus(props)}
			</IsolatedModal>
		);
	};

	const getViewByExportStatus = (
		props: FormikProps<ExportModelGetTemplateNoteOptionalParams>
	) => {
		switch (progressState) {
			case ImportStatus.Init:
				return <InitialView {...props} />;
			case ImportStatus.Done:
				return <DoneView message="Successfully finished export." />;
			case ImportStatus.Exception:
				return (
					<ExceptionView
						title="Export error"
						message="Please contact with administrator."
					/>
				);
			case ImportStatus.Loading:
				return (
					<LoadingView message="Export file in progress, please wait..." />
				);
		}
	};

	const handleExport = (
		values: any,
		actions: FormikActions<ExportModelGetTemplateNoteOptionalParams>
	) => {
		setProgressState(ImportStatus.Loading);

		const selectedValues: TemplateNoteDto[] = gridStore.getSelectedValues();

		const idsToExport = selectedValues.map((x) => x.id);

		ajaxCatch(
			() =>
				getEssityApiClient().then((api) =>
					api.exportModel.getTemplateNote({
						fileType: 'XML' as FileType,
						checked: idsToExport as string[],
						correctReport: false,
					})
				),
			(result: ExportModelGetTemplateNoteResponse) => {
				setProgressState(ImportStatus.Done);
				DownloadFileFromArray(result);
				gridStore.searching.set(true);
			},
			() => {
				setProgressState(ImportStatus.Exception);
			}
		);

		actions.setSubmitting(false);
		gridStore.setSelectedValues([]);
		gridStore.setSelectedKeys([]);
		gridStore.allRowsSelected.set(false);
	};

	const isExportEnabled = () => {
		return (
			gridStore.selectedKeys.length > 0 &&
			_.every(
				gridStore.selectedValues,
				(x: TemplateNoteDto) =>
					!x.noteCreated && !!x.noteType && x.year! < 2024
			)
		);
	};

	return (
		<>
			<Button
				type="primary"
				shape="round"
				size="large"
				onClick={() => setVisible(true)}
				disabled={!isExportEnabled()}
			>
				Payment Note
				<ArrowUpOutlined />
			</Button>

			<Formik
				validateOnChange
				validateOnBlur
				enableReinitialize
				isInitialValid={false}
				initialValues={{}}
				onSubmit={(
					values,
					actions: FormikActions<ExportModelGetTemplateNoteOptionalParams>
				) => handleExport(values, actions)}
				render={handleRender}
			/>
		</>
	);
};

export default observer(ExportPaymentNoteModal);
